import { MeiliSearch } from "meilisearch";
import retry from "async-retry";

const searchClient = (function () {
  const client = new MeiliSearch({
    host: process.env.REACT_APP_MEILISEARCH_API_ENDPOINT,
    apiKey: process.env.REACT_APP_MEILISEARCH_PUBLIC_KEY,
  });

  // const facets = ["concepts", "type", "educationalLevels"];
  const facets = [
    "concepts.subject",
    "concepts.educational-level",
    "type.name",
  ];

  const textbookFilter = [
    "type.code = textbook",
    "currentState = published",
    "sites = bcoc",
  ];

  const courseFilter = [
    "type.code = course-pack",
    "currentState = published",
    "sites = bcoc",
  ];

  const learningResourceFilter = [
    "type.code NOT IN [course-pack, textbook, supporting-document, module]",
    "currentState = published",
    "sites = bcoc",
  ];

  const isHealthy = async () => {
    try {
      return await retry(
        async () => {
          const result = await client.isHealthy();
          // We need to throw an error to retry
          if (result === false)
            throw new Error("Search engine is not healthy.");

          return true;
        },
        {
          retries: 5,
          minTimeout: 500,
        },
      );
    } catch (error) {
      return false;
    }
  };

  const refreshFacets = async () => {
    const options = {
      facets,
      hitsPerPage: 0,
      filter: [
        "type.code NOT IN [supporting-document, module]",
        "currentState = published",
        "sites = bcoc",
      ],
    };

    try {
      return await retry(
        async () => {
          // TODO @Jude, put into one meili-search, given these are all resources too, perhaps the seperation can happen client side?
          // const [courses, textbooks, learningResources] = await Promise.all([
          //   client.index("resource").search("", {
          //     ...options,
          //     filter: courseFilter,
          //   }),
          //   client.index("resource").search("", {
          //     ...options,
          //     filter: textbookFilter,
          //   }),
          //   client.index("resource").search("", {
          //     ...options,
          //     filter: learningResourceFilter,
          //   }),
          //   // client.index("learning-resources").search("", options),
          //   // client.index("textbooks").search("", options),
          // ]);

          // return {
          //   "concepts.subject": sumObjectValuesByKey(
          //     courses.facetDistribution?.["concepts.subject"],
          //     learningResources.facetDistribution?.["concepts.subject"],
          //     textbooks.facetDistribution?.["concepts.subject"],
          //   ),
          //   "type.name": sumObjectValuesByKey(
          //     courses.facetDistribution?.["type.name"],
          //     learningResources.facetDistribution?.["type.name"],
          //     textbooks.facetDistribution?.["type.name"],
          //   ),
          //   "concepts.educational-level": sumObjectValuesByKey(
          //     courses.facetDistribution?.["concepts.educational-level"],
          //     learningResources.facetDistribution?.[
          //       "concepts.educational-level"
          //     ],
          //     textbooks.facetDistribution?.["concepts.educational-level"],
          //   ),
          // };
          const response = await client.index("resource").search("", options);
          return response.facetDistribution;
        },
        {
          retries: 5,
          minTimeout: 500,
        },
      );
    } catch (error) {
      console.error("Error getting facet distribution:", error);
      return {
        "concepts.subject": {},
        "type.name": {},
        "concepts.educational-level": {},
      };
    }
  };

  const search = async (query, filter, settings) => {
    try {
      return await retry(
        async () => {
          const {
            results: [courses, textbooks, learningResources],
          } = await client.multiSearch({
            queries: [
              {
                indexUid: "resource",
                q: query,
                filter: [...filter, ...courseFilter],
                facets,
                hitsPerPage: settings.coursesLimit,
              },
              {
                indexUid: "resource",
                q: query,
                filter: [...filter, ...textbookFilter],
                facets,
                hitsPerPage: settings.textbooksLimit,
              },
              {
                indexUid: "resource",
                q: query,
                filter: [...filter, ...learningResourceFilter],
                facets,
                hitsPerPage: settings.learningResourcesLimit,
              },
            ],
          });

          return {
            query: query,
            totalHits:
              courses.totalHits +
              learningResources.totalHits +
              textbooks.totalHits,
            results: {
              courses,
              learningResources,
              textbooks,
            },
          };
        },
        {
          retries: 3,
          minTimeout: 500,
        },
      );
    } catch (error) {
      console.error("Error searching resources", error);
      return {
        query: query,
        totalHits: 0,
        results: {
          courses: { hits: [] },
          learningResources: { hits: [] },
          textbooks: { hits: [] },
        },
      };
    }
  };

  const getTopLevelSubjectsWithResourceCount = async (
    includeHierarchyInCount = false,
  ) => {
    try {
      return await retry(async () => {
        const { results } = await client.multiSearch({
          queries: [
            {
              indexUid: "concept",
              hitsPerPage: 500,
              filter: ["scheme = subject"],
            },
            {
              indexUid: "resource",
              hitsPerPage: 0,
              filter: [
                "currentState = published",
                "sites = bcoc",
                "type.code NOT IN [supporting-document, module]",
              ],
              facets: ["concepts.subject"],
            },
          ],
        });
        const [subjectResults, resourceResults] = results;

        // const allSubjectsCount =
        //   resourceResults.facetDistribution["concepts.subject"] ?? {};
        // const subjects = [];
        // for (const subject of subjectResults.hits) {
        //   const hits = allSubjectsCount[subject.name] ?? 0;
        //   const subHits =
        //     includeHierarchyInCount && subject.narrower
        //       ? subject.narrower.reduce(
        //           (acc, curr) => acc + (allSubjectsCount[curr.name] ?? 0),
        //           0,
        //         )
        //       : 0;
        //   const totalHits = hits + subHits;
        //   if (totalHits) subjects.push({ ...subject, hits: totalHits });
        // }

        const subjects = subjectResults.hits.map((subject) => ({
          ...subject,
          hits:
            resourceResults.facetDistribution["concepts.subject"][
              subject.name
            ] ?? 0,
        }));
        return subjects;
      });
    } catch (err) {
      console.error(
        "Failed to load top level subjects with count from MeiliSearch",
        err,
      );
      return [];
    }
  };

  const getTextbooksAndCoursepacksBreif = async (searchTerm) => {
    try {
      return await retry(
        async () => {
          const results = await client.multiSearch({
            queries: [
              {
                indexUid: "resource",
                q: searchTerm,
                hitsPerPage: 100,
                filter: courseFilter,
              },
              {
                indexUid: "resource",
                q: searchTerm,
                hitsPerPage: 100,
                filter: textbookFilter,
              },
            ],
          });
          return results.results;
        },
        { retries: 3, minTimeout: 500 },
      );
    } catch (err) {
      console.error("Error fetching textbooks and coursepacks brief", err);
    }
  };

  return {
    refreshFacets,
    search,
    isHealthy,
    getTopLevelSubjectsWithResourceCount,
    getTextbooksAndCoursepacksBreif,
  };
})();

export default searchClient;
